import { Link, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader, QueryState } from '../components/page';
import {
  isMissingPracticeRoute,
  usePracticeAgentJobs,
  usePracticeAgents,
  usePracticeCodeSets,
  usePrescreeningQueries,
  usePrescreeningRuns,
  useSavePracticeCodeSets,
} from '../api/hooks';
import type {
  PracticeAgentDevice,
  PracticeAgentJob,
  PracticeCodeSets,
  StructureReportLab,
} from '../api/types';
import {
  ConnectionBadge,
  Section,
  agoText,
  countText,
  dateText,
  lastImportText,
  practiceErrorText,
} from './practice-agents';
import {
  type CodeSetRow,
  codeSetRowErrors,
  codeSetsFromRows,
  parseCodes,
  rowsFromCodeSets,
} from './criteria-query';
import {
  PRESCREENING_UNAVAILABLE,
  QueriesTable,
  RunsTable,
  isUnknownRecord,
  practiceName,
  prescreeningLoadError,
  rudiErrorList,
} from './prescreening';
import { practiceDevice } from './PrescreeningPracticesPage';

export function PrescreeningPracticeRoutePage() {
  const { practiceId } = useParams({ from: '/prescreening/practices/$practiceId' });
  return <PrescreeningPracticePage practiceId={practiceId} />;
}

export function PrescreeningPracticePage({ practiceId }: { practiceId: string }) {
  const devices = usePracticeAgents(true);
  const codeSets = usePracticeCodeSets(practiceId);
  const runs = usePrescreeningRuns({ practiceId });
  const queries = usePrescreeningQueries({ practiceId });
  const ownDevices = (devices.data ?? []).filter(device => device.practiceId === practiceId);
  const device = practiceDevice(devices.data ?? [], practiceId);
  const name =
    device?.practiceName ?? queries.data?.[0]?.practiceName ?? runs.data?.[0]?.practiceName;

  return (
    <div>
      <Link
        to="/prescreening/practices"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Practices
      </Link>
      <PageHeader
        title={practiceName(name)}
        description={
          device
            ? [device.tenantName, device.teamName].filter(Boolean).join(' · ') || undefined
            : undefined
        }
      />

      <Section title="Practice computer">
        <QueryState isLoading={devices.isLoading} error={null}>
          {ownDevices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No practice computer is connected to this Practice.
            </p>
          ) : (
            <ul className="space-y-2">
              {ownDevices.map(own => (
                <li
                  key={own.id}
                  className="flex flex-wrap items-center gap-3 rounded-3xl border bg-card p-3 text-sm"
                >
                  <ConnectionBadge connection={own.connection} />
                  <Link
                    to="/practice-agents/$deviceId"
                    params={{ deviceId: own.id }}
                    className="font-medium underline-offset-4 hover:underline"
                  >
                    {own.platform} computer, app {own.appVersion}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    Last seen {agoText(own.lastSeenAt)}. {lastImportText(own.lastHeartbeat)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </QueryState>
      </Section>

      <Section
        title="Code sets"
        description="Each code set maps a concept such as eGFR to the codes this Practice's labs use. Rudi's designer uses the confirmed version in its next round, and the codes travel with every criteria query."
      >
        <QueryState
          isLoading={codeSets.isLoading}
          error={isUnknownRecord(codeSets.error) ? null : prescreeningLoadError(codeSets.error)}
        >
          {isUnknownRecord(codeSets.error) ? (
            <p role="alert" className="text-sm">
              Rudi does not know this Practice.
            </p>
          ) : codeSets.data === null ? (
            <p role="status" className="text-sm text-muted-foreground">
              {PRESCREENING_UNAVAILABLE}
            </p>
          ) : codeSets.data ? (
            <CodeSetsEditor
              key={codeSets.data.version ?? 'none'}
              current={codeSets.data}
              device={device}
            />
          ) : null}
        </QueryState>
      </Section>

      <Section title="Criteria queries">
        <QueryState isLoading={queries.isLoading} error={prescreeningLoadError(queries.error)}>
          {queries.data === null ? (
            <p className="text-sm text-muted-foreground">{PRESCREENING_UNAVAILABLE}</p>
          ) : (
            <QueriesTable
              queries={queries.data ?? []}
              showSource={false}
              empty="No criteria query for this Practice yet."
            />
          )}
        </QueryState>
      </Section>

      <Section title="Runs" description="The latest 200 runs of this Practice, newest first.">
        <QueryState isLoading={runs.isLoading} error={prescreeningLoadError(runs.error)}>
          {runs.data === null ? (
            <p className="text-sm text-muted-foreground">{PRESCREENING_UNAVAILABLE}</p>
          ) : (
            <RunsTable
              runs={runs.data ?? []}
              showSource={false}
              empty="No runs from this Practice yet."
            />
          )}
        </QueryState>
      </Section>
    </div>
  );
}

function CodeSetsEditor({
  current,
  device,
}: {
  current: PracticeCodeSets;
  device: PracticeAgentDevice | null;
}) {
  const save = useSavePracticeCodeSets();
  const [rows, setRows] = useState<CodeSetRow[]>(() => rowsFromCodeSets(current.codeSets));
  const [active, setActive] = useState(0);
  const [failure, setFailure] = useState<{ message: string; errors: string[] } | null>(null);
  const errors = codeSetRowErrors(rows);
  const changed = JSON.stringify(codeSetsFromRows(rows)) !== JSON.stringify(current.codeSets ?? {});
  const activeRow = rows[active];

  const update = (index: number, change: Partial<CodeSetRow>) =>
    setRows(previous => previous.map((row, at) => (at === index ? { ...row, ...change } : row)));

  const addRow = () => {
    setRows(previous => [...previous, { name: '', codes: '' }]);
    setActive(rows.length);
  };

  const removeRow = (index: number) => {
    setRows(previous => previous.filter((_, at) => at !== index));
    setActive(previous => (previous >= index && previous > 0 ? previous - 1 : previous));
  };

  const addCode = (code: string) => {
    if (!activeRow) return;
    const codes = parseCodes(activeRow.codes);
    if (codes.includes(code)) return;
    update(active, { codes: [...codes, code].join(', ') });
  };

  const confirm = async () => {
    setFailure(null);
    try {
      const saved = await save.mutateAsync({
        practiceId: current.practiceId,
        codeSets: codeSetsFromRows(rows),
      });
      toast.success(`Code sets confirmed as version ${saved.version}`);
    } catch (error) {
      setFailure({
        message: isMissingPracticeRoute(error, 'write')
          ? PRESCREENING_UNAVAILABLE
          : practiceErrorText(error, 'Could not confirm the code sets'),
        errors: rudiErrorList(error),
      });
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {current.version != null
          ? `Version ${current.version}, confirmed ${dateText(current.confirmedAt)}.`
          : 'Not confirmed yet.'}
      </p>
      <div className="overflow-x-auto rounded-3xl border">
        <Table aria-label="Code sets">
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">Name</TableHead>
              <TableHead>Codes, separated by commas</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index} className={index === active ? 'bg-accent/40' : undefined}>
                <TableCell>
                  <Input
                    aria-label={`Name of code set ${index + 1}`}
                    value={row.name}
                    placeholder="egfr"
                    disabled={save.isPending}
                    onFocus={() => setActive(index)}
                    onChange={event => update(index, { name: event.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    aria-label={`Codes of ${row.name.trim() || `code set ${index + 1}`}`}
                    value={row.codes}
                    placeholder="GFR, EGFR"
                    disabled={save.isPending}
                    onFocus={() => setActive(index)}
                    onChange={event => update(index, { codes: event.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove ${row.name.trim() || `code set ${index + 1}`}`}
                    disabled={save.isPending}
                    onClick={() => removeRow(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="h-16 text-center text-muted-foreground">
                  No code sets yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {errors.length > 0 && rows.length > 0 && (
        <ul
          aria-label="Code set problems"
          className="list-disc space-y-0.5 pl-5 text-xs text-destructive"
        >
          {errors.map(error => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
      {failure && (
        <div role="alert" className="text-xs text-destructive">
          <p className="font-medium">{failure.message}</p>
          {failure.errors.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {failure.errors.map(error => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={save.isPending} onClick={addRow}>
          Add code set
        </Button>
        <Button
          disabled={save.isPending || !changed || errors.length > 0}
          onClick={() => void confirm()}
        >
          {save.isPending ? 'Confirming…' : 'Confirm code sets'}
        </Button>
      </div>
      <LabHints
        device={device}
        target={activeRow ? activeRow.name.trim() || `code set ${active + 1}` : null}
        onAdd={addCode}
      />
    </div>
  );
}

/** The newest structure report that carries a lab vocabulary. */
export function latestStructureReport(
  jobs: PracticeAgentJob[]
): { job: PracticeAgentJob; labs: StructureReportLab[] } | null {
  const reports = jobs
    .filter(job => job.type === 'report.structure' && job.status === 'succeeded')
    .map(job => {
      const labs = (job.result?.observations as { labs?: unknown } | undefined)?.labs;
      return Array.isArray(labs) ? { job, labs: labs as StructureReportLab[] } : null;
    })
    .filter((report): report is { job: PracticeAgentJob; labs: StructureReportLab[] } => !!report)
    .sort((a, b) =>
      (b.job.finishedAt ?? b.job.createdAt).localeCompare(a.job.finishedAt ?? a.job.createdAt)
    );
  return reports[0] ?? null;
}

function LabHints({
  device,
  target,
  onAdd,
}: {
  device: PracticeAgentDevice | null;
  target: string | null;
  onAdd: (code: string) => void;
}) {
  if (!device)
    return (
      <p className="text-xs text-muted-foreground">
        Without a practice computer there is no structure report to suggest lab codes.
      </p>
    );
  return <DeviceLabHints device={device} target={target} onAdd={onAdd} />;
}

function DeviceLabHints({
  device,
  target,
  onAdd,
}: {
  device: PracticeAgentDevice;
  target: string | null;
  onAdd: (code: string) => void;
}) {
  const jobs = usePracticeAgentJobs(device.id);
  const [filter, setFilter] = useState('');
  const report = useMemo(() => latestStructureReport(jobs.data ?? []), [jobs.data]);
  const needle = filter.trim().toLowerCase();
  const labs = (report?.labs ?? [])
    .filter(
      lab =>
        !needle ||
        (lab.code ?? '').toLowerCase().includes(needle) ||
        (lab.name ?? '').toLowerCase().includes(needle)
    )
    .sort((a, b) => b.patients - a.patients);

  if (jobs.isLoading) return <p className="text-xs text-muted-foreground">Loading lab codes…</p>;
  if (!report)
    return (
      <p className="text-xs text-muted-foreground">
        No structure report with lab codes yet. Queue one on the{' '}
        <Link
          to="/practice-agents/$deviceId"
          params={{ deviceId: device.id }}
          className="underline underline-offset-4"
        >
          practice computer&apos;s page
        </Link>
        .
      </p>
    );

  return (
    <section aria-label="Lab codes from the structure report" className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">Lab codes from the structure report</h3>
          <p className="text-xs text-muted-foreground">
            Report of {dateText(report.job.finishedAt ?? report.job.createdAt)}. Labs with fewer
            than 5 patients are left out.{' '}
            {target ? `Add puts a code into ${target}.` : 'Add a code set to fill it from here.'}
          </p>
        </div>
        <Input
          aria-label="Search lab codes"
          placeholder="Search code or name"
          className="max-w-xs"
          value={filter}
          onChange={event => setFilter(event.target.value)}
        />
      </div>
      <div className="max-h-96 overflow-auto rounded-3xl border">
        <Table aria-label="Lab codes">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Patients</TableHead>
              <TableHead className="text-right">Entries</TableHead>
              <TableHead>Units</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {labs.map((lab, index) => (
              <TableRow key={`${lab.code ?? ''}-${lab.name ?? ''}-${index}`}>
                <TableCell className="font-mono text-xs">{lab.code ?? 'No code'}</TableCell>
                <TableCell className="text-xs">{lab.name ?? ''}</TableCell>
                <TableCell className="text-right text-xs">{countText(lab.patients)}</TableCell>
                <TableCell className="text-right text-xs">{countText(lab.entries)}</TableCell>
                <TableCell className="text-xs">
                  {(lab.units ?? []).map(unit => unit.unit ?? 'no unit').join(', ')}
                </TableCell>
                <TableCell className="text-right">
                  {lab.code && target && (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Add ${lab.code} to ${target}`}
                      onClick={() => onAdd(lab.code as string)}
                    >
                      Add
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {labs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-16 text-center text-muted-foreground">
                  No lab matches.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
